import React, { useMemo, useState } from 'react';
import ChipRows from './ChipRows';
import SectionHeader from './SectionHeader';
import TravelCard from './TravelCard';
import TRAVEL_PROJECTS from '../data/travelProjects';

// Traveler Mode's journeys, shown right below the hero: the people who reach
// this mode came for the travels, so they lead the page.
function TravelsSection({ language }) {
  const [travelSort, setTravelSort] = useState('newest');
  const [activeTravelTag, setActiveTravelTag] = useState('');

  // Travel tags use the Japanese label as the canonical key (stable across
  // the language toggle); counts drive the chip row.
  const travelTagCounts = useMemo(() => {
    const counts = new Map();
    TRAVEL_PROJECTS.forEach((trip) => {
      (trip.tags || []).forEach((tag) => {
        counts.set(tag.ja, {
          count: (counts.get(tag.ja)?.count || 0) + 1,
          label: { ja: tag.ja, en: tag.en },
        });
      });
    });
    return counts;
  }, []);

  // Most-used tags first, like the software filter (ties: by visible label).
  const sortedTravelTags = useMemo(
    () =>
      [...travelTagCounts.entries()].sort(
        (left, right) =>
          right[1].count - left[1].count ||
          left[1].label[language].localeCompare(right[1].label[language], language)
      ),
    [language, travelTagCounts]
  );

  const orderedTravelProjects = useMemo(() => {
    return [...TRAVEL_PROJECTS]
      .filter((trip) => !activeTravelTag || (trip.tags || []).some((tag) => tag.ja === activeTravelTag))
      .sort((left, right) =>
        travelSort === 'newest'
          ? right.sortKey.localeCompare(left.sortKey)
          : left.sortKey.localeCompare(right.sortKey)
      );
  }, [activeTravelTag, travelSort]);

  // JOURNEY numbers stay chronological (01 = the origin) whatever the sort.
  const travelChronoOrder = useMemo(
    () =>
      new Map(
        [...TRAVEL_PROJECTS]
          .sort((a, b) => a.sortKey.localeCompare(b.sortKey))
          .map((trip, index) => [trip.id, index + 1])
      ),
    []
  );

  const handleTravelTagClick = (tagJa) => {
    setActiveTravelTag((prev) => (prev === tagJa ? '' : tagJa));
  };

  const copy =
    language === 'ja'
      ? {
          label: 'TRAVEL LOG',
          title: '旅のプロジェクト',
          lead: '旅も、企画して、実行して、完走するプロジェクト。行程実績のあるものは、地図の上で追体験できます。',
          sortLabel: '並び順',
          newest: '新しい順',
          oldest: '古い順',
          filterLabel: '旅のタグ絞り込み',
          all: '全て',
          result: activeTravelTag
            ? `${activeTravelTag}：${orderedTravelProjects.length}件`
            : `全${orderedTravelProjects.length}件`,
          empty: '該当する旅がありません。',
        }
      : {
          label: 'TRAVEL LOG',
          title: 'Journeys as projects',
          lead: 'A journey is a project too: planned, executed, completed. Where a GPS log exists, you can replay the route on a map.',
          sortLabel: 'Sort',
          newest: 'Newest',
          oldest: 'Oldest',
          filterLabel: 'Filter journeys by tag',
          all: 'All',
          result: activeTravelTag
            ? `${orderedTravelProjects.length} shown`
            : `${orderedTravelProjects.length} journeys`,
          empty: 'No journeys match this tag.',
        };

  return (
    <section id="travels" className="travels-section" aria-label={copy.title}>
      <SectionHeader index="EX" label={copy.label} title={copy.title} lead={copy.lead} />

      <div className="works-controls">
        <div className="works-controls__sort" role="group" aria-label={copy.sortLabel}>
          <button
            type="button"
            className={`chip ${travelSort === 'newest' ? 'is-active' : ''}`}
            onClick={() => setTravelSort('newest')}
            aria-pressed={travelSort === 'newest'}
          >
            {copy.newest}
          </button>
          <button
            type="button"
            className={`chip ${travelSort === 'oldest' ? 'is-active' : ''}`}
            onClick={() => setTravelSort('oldest')}
            aria-pressed={travelSort === 'oldest'}
          >
            {copy.oldest}
          </button>
        </div>

        <ChipRows
          className="works-controls__filter"
          ariaLabel={copy.filterLabel}
          language={language}
          activeKey={activeTravelTag}
        >
          <button
            type="button"
            className={`chip ${activeTravelTag ? '' : 'is-active'}`}
            onClick={() => setActiveTravelTag('')}
            aria-pressed={!activeTravelTag}
          >
            {copy.all}
          </button>
          {sortedTravelTags.map(([tagJa, info]) => (
            <button
              key={tagJa}
              type="button"
              className={`chip ${activeTravelTag === tagJa ? 'is-active' : ''}`}
              onClick={() => handleTravelTagClick(tagJa)}
              aria-pressed={activeTravelTag === tagJa}
            >
              {language === 'ja' ? info.label.ja : info.label.en}
              <span className="chip__count">{info.count}</span>
            </button>
          ))}
        </ChipRows>
      </div>

      <p className="works-controls__result mono" role="status">
        {copy.result}
      </p>

      <div className="works-grid works-grid--travel" key={`${activeTravelTag}-${travelSort}`}>
        {orderedTravelProjects.map((trip) => (
          <TravelCard
            key={trip.id}
            trip={trip}
            language={language}
            order={travelChronoOrder.get(trip.id)}
            activeTag={activeTravelTag}
            onTagClick={handleTravelTagClick}
          />
        ))}
      </div>

      {orderedTravelProjects.length === 0 ? <p className="works-grid__empty">{copy.empty}</p> : null}
    </section>
  );
}

export default TravelsSection;
